<template>
  <div class="page novidades-page">
    <div
      class="novidades-portal"
      :class="{ 'novidades-portal--dark': darkEnabled }"
    >
      <template v-if="detailError && $route.query.idnovidade">
        <div class="novidades-detail__error">
          <v-icon large class="mb-3" color="grey"
            >fas fa-exclamation-circle</v-icon
          >
          <h3 class="novidades-portal__card-title">
            Novidade não encontrada
          </h3>
          <p class="novidades-portal__card-resumo">
            Não foi possível carregar esta novidade. Ela pode ter sido removida
            ou o link está incorreto.
          </p>
          <FormButton
            class="mt-4"
            text="Voltar para Novidades"
            type="primario"
            @click="onCloseDetail"
          />
        </div>
      </template>

      <NovidadeDetailView
        v-else-if="currentObject"
        :novidade="currentObject"
        :is-dark="darkEnabled"
        :unidade-nome="getFornecedorNome"
        @back="onCloseDetail"
        @select-tag="onTagFromDetail"
        @pdf-ready="onPdfReady"
        @export-error="onExportError"
      />

      <template v-else>
        <NovidadesPortalHeader
          :layout-mode.sync="layoutMode"
          :search-query.sync="searchQuery"
          :active-tag.sync="activeTag"
          :all-tags="allTags"
          :total-loaded="items.length"
          :unread-count="getNovidadesContador"
          :mark-all-loading="markAllLoading"
          @export-pdf="exportModalOpen = true"
          @mark-all-read="onMarkAllAsRead"
        />

        <div v-if="loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else-if="filteredItems.length === 0">
          <div class="novidades-portal__empty">
            <v-icon large class="mb-3" color="grey">fas fa-search</v-icon>
            <h3 class="novidades-portal__card-title">
              Nenhuma novidade encontrada
            </h3>
            <p class="novidades-portal__card-resumo">
              Não encontramos registros para o termo ou filtro selecionado.
            </p>
            <FormButton
              class="mt-4"
              text="Limpar todos os filtros"
              type="primario"
              @click="clearFilters"
            />
          </div>
        </template>

        <template v-else>
          <NovidadesTimelineView
            v-if="layoutMode === 'timeline'"
            :items="filteredItems"
            :recent-ids="recentIds"
            :is-dark="darkEnabled"
            @select="openDetail"
          />
          <NovidadesGridView
            v-else
            :items="filteredItems"
            :recent-ids="recentIds"
            :is-dark="darkEnabled"
            @select="openDetail"
          />
        </template>

        <div v-if="showLoadMore" class="novidades-portal__load-more">
          <FormButton
            :text="loadingMore ? 'Carregando...' : 'Ver mais'"
            type="default"
            outlined
            :disabled="loadingMore"
            @click="loadMore"
          />
        </div>
      </template>

      <ExportPeriodModal
        v-model="exportModalOpen"
        :unidade-nome="getFornecedorNome"
        @pdf-ready="onPdfReady"
        @export-error="onExportError"
      />

      <PdfPreviewModal
        v-model="pdfPreviewOpen"
        :blob="pdfPreviewBlob"
        :filename="pdfPreviewFilename"
        :title="pdfPreviewTitle"
      />
    </div>
    <transition name="slide-popup" mode="out-in">
      <router-view class="view" @close="onCloseDetail"></router-view>
    </transition>
  </div>
</template>

<script>
import FormButton from "@/components/form/FormButton";
import NovidadesPortalHeader from "./components/NovidadesPortalHeader";
import NovidadesTimelineView from "./components/NovidadesTimelineView";
import NovidadesGridView from "./components/NovidadesGridView";
import NovidadeDetailView from "./components/NovidadeDetailView";
import ExportPeriodModal from "./components/ExportPeriodModal";
import PdfPreviewModal from "./components/PdfPreviewModal";
import { mapGetters } from "vuex";
import {
  NOVIDADES_LIST,
  NOVIDADES_LOAD,
  NOVIDADES_COUNT,
  NOVIDADES_PAGE_SIZE
} from "@/store/actions/novidades";
import { ERRORS_SHOW } from "@/store/actions/errors";
import Api from "@/service/api-web";
import {
  filterNovidadesList,
  extractUniqueTags,
  resolveRecentNovidadeIds
} from "@/utils/novidades";

export default {
  name: "NovidadesTable",
  components: {
    FormButton,
    NovidadesPortalHeader,
    NovidadesTimelineView,
    NovidadesGridView,
    NovidadeDetailView,
    ExportPeriodModal,
    PdfPreviewModal
  },
  data: () => ({
    items: [],
    meta: null,
    loading: true,
    loadingMore: false,
    loadingDetail: false,
    currentObject: null,
    detailError: false,
    layoutMode: "timeline",
    searchQuery: "",
    activeTag: null,
    exportModalOpen: false,
    pdfPreviewOpen: false,
    pdfPreviewBlob: null,
    pdfPreviewFilename: "novidades.pdf",
    pdfPreviewTitle: "Pré-visualização do Relatório em PDF (A4)",
    markAllLoading: false
  }),
  computed: {
    ...mapGetters(["darkEnabled", "getFornecedorNome", "getNovidadesContador"]),
    filteredItems() {
      return filterNovidadesList(this.items, {
        searchQuery: this.searchQuery,
        activeTag: this.activeTag
      });
    },
    allTags() {
      return extractUniqueTags(this.items);
    },
    recentIds() {
      return resolveRecentNovidadeIds(this.items);
    },
    showLoadMore() {
      if (this.loading || this.currentObject || this.detailError) return false;
      if (this.meta && typeof this.meta.hasMore === "boolean") {
        return this.meta.hasMore;
      }
      if (this.meta && this.meta.total != null) {
        return this.items.length < this.meta.total;
      }
      return false;
    }
  },
  watch: {
    "$route.query.idnovidade": {
      handler(idnovidade) {
        if (idnovidade) {
          this.openDetail(idnovidade);
        } else if (!this.loadingDetail) {
          this.currentObject = null;
          this.detailError = false;
        }
      }
    }
  },
  created() {
    this.fetchInitial().then(() => {
      const idnovidade = this.$route.query.idnovidade;
      if (idnovidade) {
        this.openDetail(idnovidade);
      }
    });
  },
  methods: {
    fetchInitial() {
      this.loading = true;
      return this.fetchPage(0, false).finally(() => {
        this.loading = false;
      });
    },
    fetchPage(offset, append) {
      return this.$store
        .dispatch(NOVIDADES_LIST, {
          limit: NOVIDADES_PAGE_SIZE,
          offset
        })
        .then(result => {
          const data = (result && result.data) || [];
          this.meta = (result && result.meta) || null;
          if (append) {
            this.items = [...this.items, ...data];
          } else {
            this.items = data;
          }
        });
    },
    loadMore() {
      if (this.loadingMore || !this.showLoadMore) return;
      this.loadingMore = true;
      const offset = this.items.length;
      this.fetchPage(offset, true).finally(() => {
        this.loadingMore = false;
      });
    },
    clearFilters() {
      this.searchQuery = "";
      this.activeTag = null;
    },
    openDetail(idnovidade) {
      if (
        this.currentObject &&
        this.currentObject.idnovidade === idnovidade &&
        !this.detailError
      ) {
        return;
      }
      this.detailError = false;
      this.loadingDetail = true;
      const query = { ...this.$route.query, idnovidade };
      this.$router.replace({ query }).catch(() => {});
      this.$store
        .dispatch(NOVIDADES_LOAD, idnovidade)
        .then(data => {
          this.currentObject = data;
          this.$store.dispatch(NOVIDADES_COUNT);
        })
        .catch(() => {
          this.currentObject = null;
          this.detailError = true;
        })
        .finally(() => {
          this.loadingDetail = false;
        });
    },
    onCloseDetail() {
      this.currentObject = null;
      this.detailError = false;
      const q = { ...this.$route.query };
      delete q.idnovidade;
      this.$router.replace({ query: q }).catch(() => {});
    },
    onTagFromDetail(tag) {
      this.activeTag = tag;
      this.onCloseDetail();
    },
    onPdfReady(payload) {
      this.pdfPreviewBlob = payload.blob;
      this.pdfPreviewFilename = payload.filename || "novidades.pdf";
      this.pdfPreviewTitle =
        payload.title || "Pré-visualização do Relatório em PDF (A4)";
      this.pdfPreviewOpen = true;
    },
    onExportError(message) {
      this.$store.dispatch(ERRORS_SHOW, {
        response: { data: { message } }
      });
    },
    onMarkAllAsRead() {
      if (this.markAllLoading || this.getNovidadesContador <= 0) return;
      this.markAllLoading = true;
      Api.v4.novidades
        .marcarTodasLidas()
        .then(() => this.$store.dispatch(NOVIDADES_COUNT))
        .catch(err => this.$store.dispatch(ERRORS_SHOW, err))
        .finally(() => {
          this.markAllLoading = false;
        });
    }
  }
};
</script>

<style lang="scss" src="./novidades-portal.scss"></style>
<style lang="scss" src="./novidades-detail.scss"></style>

<style lang="scss" scoped>
.novidades-page {
  width: 100%;
}
</style>
